import {
    differenceInMonths,
    parseISO,
    differenceInDays,
    startOfYear,
    endOfYear,
    isWithinInterval,
} from "date-fns";
import type {
    Property,
    Financing,
    PropertyMetrics,
    DealCheck,
    DealCheckResult,
    RenovationProject,
    RenovationCost,
    Purchase,
    Sale,
    FlipMetrics,
    Rental,
    RentalCosts,
    VacancyPeriod,
    BuyHoldMetrics,
} from "@/types";

// Berechnet aktive Finanzierung-Summen
function getActiveFinancingTotals(financings: Financing[] = []) {
  const active = financings.filter(f => f.is_active);

  const totalLoan = active.reduce((sum, f) => sum + f.loan_amount, 0);
  const weightedInterest = active.length > 0
    ? active.reduce((sum, f) => sum + (f.interest_rate * f.loan_amount), 0) / totalLoan
    : 0;
  const weightedRepayment = active.length > 0
    ? active.reduce((sum, f) => sum + (f.repayment_rate * f.loan_amount), 0) / totalLoan
    : 0;

  return { totalLoan, weightedInterest, weightedRepayment };
}

// Monatliche Annuität berechnen
function calculateMonthlyPayment(loanAmount: number, interestRate: number, repaymentRate: number): number {
  // interestRate und repaymentRate sind in % (z.B. 3.5 für 3,5%)
  const annualPayment = loanAmount * ((interestRate + repaymentRate) / 100);
  return annualPayment / 12;
}

// Hauptfunktion: Alle Kennzahlen berechnen
export function calculatePropertyMetrics(property: Property): PropertyMetrics {
  // Gesamtinvestition berechnen (inkl. aller Kosten)
  const purchasePrice = property.purchase_price || 0;
  const additionalCosts =
      (property.cost_transfer_tax || 0) +
          (property.cost_notary || 0) +
          (property.cost_land_registry || 0) +
          (property.cost_broker || 0) +
          (property.cost_other || 0) ||
      property.additional_costs ||
      0;

  const renovationCosts = property.renovation_cost_total || 0;

  // Haltekosten
  let holdingCosts = 0;
  if (property.holding_period_start && property.holding_cost_monthly) {
      const start = parseISO(property.holding_period_start);
      const end = property.holding_period_end
          ? parseISO(property.holding_period_end)
          : new Date();
      const months = Math.max(1, differenceInMonths(end, start));
      holdingCosts = property.holding_cost_monthly * months;
  }

  const totalInvestment =
      purchasePrice + additionalCosts + renovationCosts + holdingCosts;

  // Laufende Kosten (monatlich, nicht umlagefähig)
  const runningCostsMonthly =
      (property.running_cost_hausgeld_non_allocable || 0) +
      (property.running_cost_reserve || 0) +
      (property.running_cost_management || 0) +
      (property.running_cost_insurance || 0) +
      (property.running_cost_property_tax || 0) +
      (property.running_cost_other || 0);

  const rentCold = property.rent_cold || 0;
  const jahresmieteKalt = rentCold * 12;

  // Bruttomietrendite = Jahresmiete / Kaufpreis
  const bruttoRendite =
      purchasePrice > 0 ? (jahresmieteKalt / purchasePrice) * 100 : null;

  // Nettomietrendite = Jahresmiete / Gesamtinvestition
  const nettoRendite =
      totalInvestment > 0 ? (jahresmieteKalt / totalInvestment) * 100 : null;

  // Finanzierung
  const financings = property.financings || [];
  const activeFinancings = financings.filter((f) => f.is_active);
  const { totalLoan, weightedInterest, weightedRepayment } = getActiveFinancingTotals(activeFinancings);
  
  const monthlyFinancing = totalLoan > 0
    ? calculateMonthlyPayment(totalLoan, weightedInterest, weightedRepayment)
    : 0;
    
  const jahresZinsen = totalLoan > 0
    ? totalLoan * (weightedInterest / 100)
    : 0;

  // Eigenkapital
  const eigenkapital = totalInvestment - totalLoan;

  // Eigenkapitalrendite = (Jahresmiete - Zinsen - laufende Kosten) / Eigenkapital
  const annualRunningCosts = runningCostsMonthly * 12;
  const eigenkapitalRendite =
      eigenkapital > 0 && jahresmieteKalt > 0
          ? ((jahresmieteKalt - jahresZinsen - annualRunningCosts) /
                eigenkapital) *
            100
          : null;

  // Cashflow
  const cashflowMonatlich =
      rentCold > 0 ? rentCold - runningCostsMonthly - monthlyFinancing : null;

  return {
      bruttoRendite,
      nettoRendite,
      eigenkapitalRendite,
      cashflowMonatlich,
      eigenkapital,
      restschuld: totalLoan,
      totalInvestment,
      runningCostsMonthly,
  };
}

export function calculateDealCheck(check: DealCheck): DealCheckResult | null {
    const exitValue = check.estimated_exit_value;
    if (!exitValue || exitValue <= 0) return null;

    const totalAdditionalCosts =
        (check.cost_transfer_tax || 0) +
        (check.cost_notary || 0) +
        (check.cost_land_registry || 0) +
        (check.cost_broker || 0) +
        (check.cost_other || 0);

    const renovation = check.estimated_renovation || 0;
    const purchase = check.asking_price || 0;
    const allInTotal = purchase + totalAdditionalCosts + renovation;
    const allInPercent = (allInTotal / exitValue) * 100;

    const dealStatus =
        allInPercent < 70 ? "go" : allInPercent <= 75 ? "maybe" : "stop";

    const fixedCosts = totalAdditionalCosts + renovation;
    const maxPurchase70 = exitValue * 0.7 - fixedCosts;
    const maxPurchase75 = exitValue * 0.75 - fixedCosts;
    const potentialProfit = exitValue - allInTotal;

    return {
        totalAdditionalCosts,
        allInTotal,
        allInPercent,
        dealStatus,
        maxPurchase70,
        maxPurchase75,
        potentialProfit,
    };
}

// Portfolio-Aggregation
export function calculatePortfolioMetrics(properties: Property[]) {
  const owned = properties.filter(p => p.status === 'in_portfolio' || p.status === 'purchased' || p.status === 'in_renovation');

  const totalValue = owned.reduce((sum, p) => sum + (p.current_value || p.purchase_price || 0), 0);
  const totalPurchasePrice = owned.reduce((sum, p) => sum + (p.purchase_price || 0), 0);
  const totalRentCold = owned.reduce((sum, p) => sum + (p.rent_cold || 0), 0);

  const totalLoan = owned.reduce((sum, p) => {
    const activeLoan = (p.financings || [])
      .filter(f => f.is_active)
      .reduce((s, f) => s + f.loan_amount, 0);
    return sum + activeLoan;
  }, 0);

  const totalEquity = totalValue - totalLoan;

  // Aggregierter Cashflow
  const totalCashflow = owned.reduce((sum, p) => {
    const metrics = calculatePropertyMetrics(p);
    return sum + (metrics.cashflowMonatlich || 0);
  }, 0);

      return {
      totalValue,
      totalPurchasePrice,
      totalRentCold,
      totalLoan,
      totalEquity,
      totalCashflow,
      propertyCount: owned.length,
      watchlistCount: properties.filter(p => p.status === 'watchlist').length,
    };
  }
  
  export function calculateFlipMetrics(
      project: RenovationProject,
      costs: RenovationCost[],
      purchase: Purchase | null,
      sale: Sale | null,
      financings: Financing[],
  ): FlipMetrics | null {
      // Budget-Analyse
      const totalPlanned = costs.reduce(
          (sum, c) => sum + (c.planned_amount || 0),
          0,
      );
      const totalActual = costs.reduce(
          (sum, c) => sum + (c.actual_amount || 0),
          0,
      );
      const budgetUsedPercent =
          totalPlanned > 0 ? (totalActual / totalPlanned) * 100 : 0;
      const budgetVariance = totalActual - totalPlanned;
  
      // Zeit-Analyse
      const plannedStart = project.planned_start
          ? parseISO(project.planned_start)
          : null;
      const plannedEnd = project.planned_sale_end
          ? parseISO(project.planned_sale_end)
          : null;
      const actualStart = project.actual_start
          ? parseISO(project.actual_start)
          : null;
      const actualEnd = project.actual_sale_end
          ? parseISO(project.actual_sale_end)
          : sale?.sale_date
            ? parseISO(sale.sale_date)
            : null;
  
      const plannedDurationMonths =
          plannedStart && plannedEnd
              ? differenceInMonths(plannedEnd, plannedStart)
              : 0;
      const actualDurationMonths =
          actualStart && actualEnd
              ? differenceInMonths(actualEnd, actualStart)
              : 0;
      const timeVarianceMonths = actualDurationMonths - plannedDurationMonths;
  
      // Endabrechnung (nur wenn verkauft)
      if (!sale || !purchase) {
          return {
              totalPlanned,
              totalActual,
              budgetUsedPercent,
              budgetVariance,
              plannedDurationMonths,
              actualDurationMonths,
              timeVarianceMonths,
              totalRevenue: 0,
              totalCosts: 0,
              netProfit: 0,
              profitPerMonth: 0,
              profitPerMonthCapital: 0,
              returnOnEquity: 0,
          };
      }
  
      // Einnahmen
      const totalRevenue = sale.sale_price;
  
      // Ausgaben
      const purchaseCosts =
          purchase.final_price +
          (purchase.actual_transfer_tax || 0) +
          (purchase.actual_notary || 0) +
          (purchase.actual_land_registry || 0) +
          (purchase.actual_broker || 0) +
          (purchase.actual_other || 0);
  
      const renovationCosts = totalActual;
  
      const monthlyCosts =
          (project.monthly_interest || 0) +
          (project.monthly_utilities || 0) +
          (project.monthly_insurance || 0) +
          (project.monthly_other || 0);
      const holdingCosts = monthlyCosts * actualDurationMonths;
  
      const saleCosts =
          (sale.broker_fee || 0) +
          (sale.prepayment_penalty || 0) +
          (sale.other_costs || 0);
  
      const totalCosts =
          purchaseCosts + renovationCosts + holdingCosts + saleCosts;
  
      // Gewinn
      const netProfit = totalRevenue - totalCosts;
      const profitPerMonth =
          actualDurationMonths > 0 ? netProfit / actualDurationMonths : 0;
  
      // Eigenkapital berechnen
      const totalLoan = financings
          .filter((f) => f.is_active)
          .reduce((sum, f) => sum + f.loan_amount, 0);
      const equity = purchaseCosts + renovationCosts - totalLoan;
  
      // Killer-Metrik: Gewinn pro Monat Kapitalbindung
      const profitPerMonthCapital =
          equity > 0 && actualDurationMonths > 0
              ? netProfit / actualDurationMonths
              : 0;
  
      // Return on Equity
      const returnOnEquity = equity > 0 ? (netProfit / equity) * 100 : 0;
  
          return {
              totalPlanned,
              totalActual,
              budgetUsedPercent,
              budgetVariance,
              plannedDurationMonths,
              actualDurationMonths,
              timeVarianceMonths,
              totalRevenue,
              totalCosts,
              netProfit,
              profitPerMonth,
              profitPerMonthCapital,
              returnOnEquity,
          };
      }
      
      export function calculateBuyHoldMetrics(
          property: Property,
          currentRental: Rental | null,
          costs: RentalCosts | null,
          financings: Financing[],
          vacancies: VacancyPeriod[],
      ): BuyHoldMetrics {
          // Mieteinnahmen
          const currentRentCold = currentRental?.rent_cold || 0;
          const currentRentWarm =
              currentRentCold + (currentRental?.rent_utilities || 0);
          const rentPerSqm =
              property.living_area_sqm && currentRentCold > 0
                  ? currentRentCold / 100 / property.living_area_sqm // Cent zu Euro
                  : null;
      
          // Laufende Kosten
          const hausgeldNotAllocable = costs?.hausgeld_not_allocable || 0;
          const insurance = costs?.insurance || 0;
          const managementFee = costs?.management_fee || 0;
          const otherCosts = costs?.other || 0;
          const reserveContribution = costs?.reserve_contribution || 0;
      
          const totalMonthlyCosts =
              hausgeldNotAllocable + insurance + managementFee + otherCosts;
          const nonAllocableCosts = hausgeldNotAllocable;
      
          // Finanzierung
          const activeFinancings = financings.filter((f) => f.is_active);
          const monthlyFinancing = activeFinancings.reduce((sum, f) => {
              const annualPayment =
                  f.loan_amount * ((f.interest_rate + f.repayment_rate) / 100);
              return sum + annualPayment / 12;
          }, 0);
      
          // Cashflow-Kaskade
          const grossCashflow = currentRentCold;
          const netCashflowBeforeFinancing = grossCashflow - totalMonthlyCosts;
          const netCashflowAfterFinancing =
              netCashflowBeforeFinancing - monthlyFinancing;
          const netCashflowAfterReserves =
              netCashflowAfterFinancing - reserveContribution;
      
          // Renditen
          const purchasePrice = property.purchase_price || 0;
          const additionalCosts = property.additional_costs || 0;
          const totalInvestment = purchasePrice + additionalCosts;
          const annualRent = currentRentCold * 12;
      
          const bruttoRendite =
              purchasePrice > 0 ? (annualRent / purchasePrice) * 100 : null;
      
          const nettoRendite =
              totalInvestment > 0 ? (annualRent / totalInvestment) * 100 : null;
      
          // Eigenkapitalrendite
          const totalLoan = activeFinancings.reduce(
              (sum, f) => sum + f.loan_amount,
              0,
          );
          const equity = totalInvestment - totalLoan;
          const annualInterest = activeFinancings.reduce(
              (sum, f) => sum + (f.loan_amount * f.interest_rate) / 100,
              0,
          );
          const annualCashflowAfterInterest =
              annualRent - annualInterest - totalMonthlyCosts * 12;
      
          const eigenkapitalRendite =
              equity > 0 ? (annualCashflowAfterInterest / equity) * 100 : null;
      
          // Leerstand berechnen
          const now = new Date();
          const thisYear = { start: startOfYear(now), end: endOfYear(now) };
      
          let vacancyDaysTotal = 0;
          let vacancyDaysThisYear = 0;
      
          vacancies.forEach((v) => {
              const start = parseISO(v.start_date);
              const end = v.end_date ? parseISO(v.end_date) : now;
              const days = differenceInDays(end, start);
      
              vacancyDaysTotal += days;
      
              // Tage in diesem Jahr
              if (
                  isWithinInterval(start, thisYear) ||
                  isWithinInterval(end, thisYear)
              ) {
                  const effectiveStart =
                      start < thisYear.start ? thisYear.start : start;
                  const effectiveEnd = end > thisYear.end ? thisYear.end : end;
                  vacancyDaysThisYear += differenceInDays(
                      effectiveEnd,
                      effectiveStart,
                  );
              }
          });
      
          const daysThisYear = differenceInDays(now, thisYear.start);
          const occupancyRate =
              daysThisYear > 0
                  ? ((daysThisYear - vacancyDaysThisYear) / daysThisYear) * 100
                  : 100;
      
          return {
              currentRentCold,
              currentRentWarm,
              rentPerSqm,
              totalMonthlyCosts,
              nonAllocableCosts,
              grossCashflow,
              netCashflowBeforeFinancing,
              netCashflowAfterFinancing,
              netCashflowAfterReserves,
              bruttoRendite,
              nettoRendite,
              eigenkapitalRendite,
              vacancyDaysTotal,
              vacancyDaysThisYear,
              occupancyRate,
              marketRentEstimate: null, // Später: Marktdaten
              rentUpsidePotential: null,
          };
      }